import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { VideoCamera, ImageSquare, Broadcast, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const asset = (u) => (!u ? "" : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

export default function AdminMeet() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await api.get("/settings/platform")).data });
  const s = data?.settings;
  const save = async (patch) => {
    setSaving(true);
    try { await api.put("/settings/platform", patch); toast.success("Saved"); qc.invalidateQueries({ queryKey: ["platform-settings"] }); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const uploadBg = async (file) => {
    if (!file) return;
    setBgBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/meet/background", fd, { headers: { "Content-Type": "multipart/form-data" } }); await save({ meet_background_url: data.url }); }
    catch { toast.error("Upload failed"); } finally { setBgBusy(false); }
  };
  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-meet">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Meet Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Global defaults for every video meeting. Hosts can override these inside a meeting.</p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><VideoCamera size={20} weight="duotone" /></div>
          <div><h2 className="font-display text-lg font-semibold">Room defaults</h2><p className="text-sm text-muted-foreground">Waiting room and how many people show on screen.</p></div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="meet-waiting-room" checked={!!s.waiting_room} onChange={(e) => save({ waiting_room: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Waiting room on by default — hosts must admit guests</span>
        </label>
        <div className="mt-4">
          <p className="text-sm font-medium">Default screen layout</p>
          <p className="text-xs text-muted-foreground">Full screen spotlights one person; divided shows everyone in a grid.</p>
          <button data-testid="meet-fullscreen" onClick={() => save({ meet_fullscreen: !s.meet_fullscreen })} className={`mt-2 rounded-full px-4 py-1.5 text-sm font-semibold ${s.meet_fullscreen ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{s.meet_fullscreen ? "Full screen" : "Divided screen"}</button>
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><ImageSquare size={18} weight="duotone" /> Default background</h2>
        <p className="mt-1 text-sm text-muted-foreground">Shown as the stage backdrop in meetings that don't set their own.</p>
        <div className="mt-3 flex items-center gap-3">
          <label data-testid="meet-bg-upload" className="flex cursor-pointer items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/80"><ImageSquare size={15} /> {bgBusy ? "Uploading…" : "Upload image"}<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} /></label>
          {s.meet_background_url && <button data-testid="meet-bg-clear" onClick={() => save({ meet_background_url: "" })} className="rounded-full border border-white/10 px-3 py-2 text-sm">Clear</button>}
        </div>
        {s.meet_background_url && <img data-testid="meet-bg-preview" src={asset(s.meet_background_url)} alt="background" className="mt-3 h-32 w-full rounded-xl object-cover" />}
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Broadcast size={18} weight="duotone" /> Live-stream keys</h2>
        <p className="mt-1 text-sm text-muted-foreground">Save your YouTube / Facebook stream keys to broadcast meetings out (streaming server required — coming soon).</p>
        <div className="mt-3 space-y-3">
          <Field label="YouTube stream key" testid="meet-youtube-key" defaultValue={s.meet_youtube_key || ""} onSave={(v) => save({ meet_youtube_key: v })} />
          <Field label="Facebook stream key" testid="meet-facebook-key" defaultValue={s.meet_facebook_key || ""} onSave={(v) => save({ meet_facebook_key: v })} />
          <Field label="Custom RTMP URL" testid="meet-rtmp-url" defaultValue={s.meet_rtmp_url || ""} onSave={(v) => save({ meet_rtmp_url: v })} placeholder="rtmp://…" />
        </div>
      </section>

      {saving && <p className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin" /> Saving…</p>}
    </div>
  );
}

function Field({ label, testid, defaultValue, onSave, placeholder }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input data-testid={testid} defaultValue={defaultValue} placeholder={placeholder}
          onBlur={(e) => { if (e.target.value !== defaultValue) onSave(e.target.value.trim()); }}
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <CheckCircle size={14} weight="fill" className="text-primary" />
      </div>
    </div>
  );
}
